import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Bell, BellRing, BellOff, CheckCircle2, Share, PlusSquare, Smartphone, Loader2, AlertTriangle, Send } from 'lucide-react';
import {
  isIOS, isAndroid, isStandalone, pushSupported, permissionState,
  subscribeToPush, unsubscribeFromPush, getExistingSubscription,
} from '../lib/push';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const Step = ({ n, icon: Icon, children }) => (
  <li className="flex items-start gap-3">
    <span className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">{n}</span>
    <span className="text-sm text-slate-800 leading-relaxed flex items-center gap-1.5 flex-wrap">
      {Icon && <Icon className="w-4 h-4 text-blue-700 inline" />}{children}
    </span>
  </li>
);

// Self-contained "turn on push notifications" flow with platform-specific coaching.
// compact: tighter layout for embedding (announcement gate); onEnabled fires after a successful subscribe.
export default function PushSetupCard({ compact = false, onEnabled }) {
  const [perm, setPerm] = useState(permissionState());
  const [thisDevice, setThisDevice] = useState(false);
  const [devices, setDevices] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);

  const ios = isIOS();
  const installed = isStandalone();
  const supported = pushSupported();
  const iosNeedsInstall = ios && !installed;

  const refresh = async () => {
    setPerm(permissionState());
    try {
      const sub = await getExistingSubscription();
      const r = await axios.get(`${API}/push/status`, { ...auth(), params: { endpoint: sub?.endpoint || '' } });
      setThisDevice(!!(sub && r.data?.this_device));
      setDevices(r.data?.devices ?? 0);
    } catch { setDevices(0); }
  };

  useEffect(() => { refresh(); }, []);

  const enable = async () => {
    setBusy(true);
    const res = await subscribeToPush();
    setBusy(false);
    if (res.ok) {
      toast.success('Notifications are on for this device');
      await refresh();
      onEnabled?.();
    } else if (res.reason === 'ios_not_installed') {
      toast.error('On iPhone, add 365Ops to your Home Screen first (steps below)');
    } else if (res.reason === 'denied') {
      setPerm('denied');
      toast.error('Notifications were blocked — see how to fix it below');
    } else if (res.reason === 'unsupported') {
      toast.error("This browser can't do push notifications — open 365Ops in Chrome (Android) or from your Home Screen (iPhone)");
    } else {
      toast.error(res.error || 'Could not turn on notifications');
    }
  };

  const disable = async () => {
    setBusy(true);
    await unsubscribeFromPush();
    setBusy(false);
    toast('Notifications turned off on this device');
    refresh();
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      await axios.post(`${API}/push/test`, {}, auth());
      toast.success('Test sent — you should see it pop up in a second');
    } catch (e) { toast.error(e.response?.data?.detail || 'Test failed'); }
    setTesting(false);
  };

  const on = perm === 'granted' && thisDevice;

  return (
    <div className={`rounded-2xl border ${on ? 'border-emerald-200 bg-emerald-50' : 'border-blue-200 bg-blue-50'} ${compact ? 'p-4' : 'p-5 sm:p-6'}`} data-testid="push-setup-card">
      <div className="flex items-start gap-3">
        <div className={`p-2.5 rounded-xl ${on ? 'bg-emerald-600' : 'bg-blue-600'}`}>
          {on ? <BellRing className="w-5 h-5 text-white" /> : <Bell className="w-5 h-5 text-white" />}
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-slate-900 font-bold text-base" data-testid="push-status-title">
            {on ? 'Notifications are ON for this device' : 'Turn on dispatch notifications'}
          </h3>
          <p className="text-sm text-slate-700 mt-0.5">
            {on
              ? 'You’ll get a pop-up the moment a dispatch is posted or someone replies in your job chat.'
              : 'Dispatches now come through the 365Ops app instead of texts. Turn this on so you never miss a job.'}
          </p>
          {devices !== null && devices > 0 && !on && (
            <p className="text-xs text-slate-500 mt-1" data-testid="push-other-devices">Already on for {devices} other device{devices !== 1 ? 's' : ''} of yours.</p>
          )}
        </div>
      </div>

      {/* iPhone: must be installed to Home Screen first */}
      {!on && iosNeedsInstall && (
        <div className="mt-4 rounded-xl bg-white border border-slate-200 p-4" data-testid="push-ios-install-steps">
          <p className="text-sm font-semibold text-amber-700 flex items-center gap-2 mb-3"><Smartphone className="w-4 h-4" /> iPhone / iPad — 3 quick steps</p>
          <ol className="space-y-2.5">
            <Step n={1} icon={Share}>Tap the <b>Share</b> button in Safari (the square with the arrow, bottom of the screen).</Step>
            <Step n={2} icon={PlusSquare}>Scroll down and tap <b>Add to Home Screen</b>, then <b>Add</b>.</Step>
            <Step n={3} icon={Bell}>Open <b>365Ops from your Home Screen</b>, come back to this page and tap <b>Turn on notifications</b>.</Step>
          </ol>
          <p className="text-[11px] text-slate-500 mt-3">Needs iOS 16.4 or newer. Notifications only work from the Home Screen app, not from the Safari tab.</p>
        </div>
      )}

      {/* Android / desktop hint */}
      {!on && !ios && perm === 'default' && (
        <p className="mt-3 text-xs text-slate-500 flex items-center gap-1.5" data-testid="push-android-hint">
          <Smartphone className="w-3.5 h-3.5" /> {isAndroid() ? 'Tap the button, then choose Allow when your phone asks.' : 'Click the button, then Allow in your browser.'}
        </p>
      )}

      {/* Blocked */}
      {perm === 'denied' && (
        <div className="mt-4 rounded-xl bg-red-50 border border-red-200 p-4" data-testid="push-denied-help">
          <p className="text-sm font-semibold text-red-700 flex items-center gap-2 mb-2"><AlertTriangle className="w-4 h-4" /> Notifications are blocked for 365Ops</p>
          <ul className="text-xs text-slate-700 space-y-1.5 list-disc pl-5">
            {ios ? (
              <li>iPhone: <b>Settings → Notifications → 365Ops → Allow Notifications</b>, then come back and tap the button.</li>
            ) : isAndroid() ? (
              <li>Android: <b>Settings → Apps → Chrome (or 365Ops) → Notifications → Allow</b>, or tap the lock icon in the address bar → Permissions → Notifications.</li>
            ) : (
              <li>Click the lock icon next to the address bar → Notifications → Allow, then reload.</li>
            )}
          </ul>
        </div>
      )}

      {!supported && !iosNeedsInstall && (
        <p className="mt-3 text-xs text-amber-700" data-testid="push-unsupported">This browser can’t receive push notifications. Use Chrome on Android, or add 365Ops to your Home Screen on iPhone.</p>
      )}

      <div className={`flex flex-wrap gap-2 ${compact ? 'mt-4' : 'mt-5'}`}>
        {!on ? (
          <button onClick={enable} disabled={busy || perm === 'denied' || iosNeedsInstall}
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold px-5 py-3 rounded-xl text-sm transition-colors"
            data-testid="push-enable-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <BellRing className="w-4 h-4" />} Turn on notifications
          </button>
        ) : (
          <>
            <button onClick={sendTest} disabled={testing}
              className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-60 text-white font-semibold px-4 py-2.5 rounded-xl text-sm transition-colors"
              data-testid="push-test-btn">
              {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send me a test
            </button>
            <button onClick={disable} disabled={busy}
              className="inline-flex items-center gap-2 text-slate-500 hover:text-red-700 px-3 py-2.5 rounded-xl text-xs transition-colors"
              data-testid="push-disable-btn">
              <BellOff className="w-3.5 h-3.5" /> Turn off on this device
            </button>
          </>
        )}
        {on && <span className="inline-flex items-center gap-1 text-emerald-700 text-xs ml-auto"><CheckCircle2 className="w-4 h-4" /> Ready</span>}
      </div>
    </div>
  );
}
